import React, { useState, useRef, useEffect, useCallback } from 'react';
import ThreadsService from '../../services/ThreadsService';
import './ConversationModal.css';

const ConversationModal = ({ isOpen, onClose, threadId, assistantName, assistantId, useOpenAI = false, colegioId = null }) => {
  const [conversation, setConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const chatContainerRef = useRef(null);



  const loadConversation = useCallback(async () => {
    if (!threadId || !assistantId || !colegioId) {
      setError('Faltan datos necesarios para cargar la conversación');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      
      const response = await ThreadsService.getThreadConversation(
        assistantId,
        threadId,
        100,
        { usarOpenAI: useOpenAI, idColegio: colegioId }
      );
      
      if (response.ok && response.mensajes) {
        // Procesar los mensajes paral formato esperado por el componente
        const processedMessages = response.mensajes.map(msg => ({
          id: msg.idMensaje,
          content: processMessageContent(msg.contenido),
          role: msg.rol,
          timestamp: new Date(msg.fechaEnvio)
        }));
        
        setConversation(processedMessages);
      } else {
        throw new Error('Error al cargar la conversación');
      }
      
    } catch (error) {
      console.error('❌ Error cargando conversación:', error);
      setError(error.message || 'Error al cargar la conversación');
      setConversation([]);
    } finally {
      setIsLoading(false);
    }
  }, [threadId, assistantId, colegioId, useOpenAI]);

  // Función para procesar el contenido HTML de los mensajes del asistente
  const processMessageContent = (content) => {
    if (!content) return '';
    
    // Si el contenido viene envuelto en ```html, extraer solo el HTML
    if (content.includes('```html')) {
      const htmlMatch = content.match(/```html\s*([\s\S]*?)\s*```/);
      if (htmlMatch) {
        // Extraer solo el contenido del body si existe
        const bodyMatch = htmlMatch[1].match(/<body[^>]*>([\s\S]*?)<\/body>/);
        if (bodyMatch) {
          return bodyMatch[1].trim();
        }
        // Si no hay body, devolver todo el HTML extraído
        return htmlMatch[1].trim();
      }
    }
    
    // Si no hay marcadores de código, devolver el contenido tal como está
    return content;
  };

  // Cargar conversación cuando se abre el modal
  useEffect(() => {
    if (isOpen && threadId && assistantId) {
      loadConversation();
    }
  }, [isOpen, threadId, assistantId, loadConversation]);

  // Auto-scroll al final cuando se agregan mensajes
  useEffect(() => {
    if (chatContainerRef.current && conversation.length > 0) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [conversation]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) {
    return null;
  }


  return (
    <div className="conversation-modal-overlay" onClick={onClose}>
      <div className="conversation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="conversation-modal-header">
          <div className="conversation-modal-title">
            <i className="fas fa-comments"></i>
            <div>
              <h3>Conversación Completa</h3>
              <p className="conversation-subtitle">
                {assistantName && `Asistente: ${assistantName}`}
              </p>
            </div>
          </div>
          <button className="conversation-modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="conversation-modal-body">
          {isLoading ? (
            <div className="conversation-loading">
              <div className="spinner"></div>
              <p>Cargando conversación...</p>
            </div>
          ) : error ? (
            <div className="conversation-empty">
              <i className="fas fa-exclamation-triangle"></i>
              <p>Error al cargar la conversación: {error}</p>
            </div>
          ) : (
            <div className="conversation-chat-container" ref={chatContainerRef}>
              {conversation.length === 0 ? (
                <div className="conversation-empty">
                  <i className="fas fa-comment-slash"></i>
                  <p>No se encontraron mensajes en esta conversación</p>
                </div>
              ) : (
                <div className="conversation-messages">
                  {conversation.map((msg) => (
                    <div
                      key={msg.id}
                      className={`conversation-message ${msg.role === 'user' ? 'user-message' : 'assistant-message'}`}
                    >
                      <div className="conversation-message-header">
                        <div className="conversation-message-avatar">
                          <i className={msg.role === 'user' ? 'fas fa-user-graduate' : 'fas fa-robot'}></i>
                        </div>
                        <div className="conversation-message-info">
                          <span className="conversation-message-author">
                            {msg.role === 'user' ? 'Estudiante' : (assistantName || 'Asistente IA')}
                          </span>
                          <span className="conversation-message-time">
                            {formatTimestamp(msg.timestamp)}
                          </span>
                        </div>
                      </div>
                      <div 
                        className="conversation-message-content"
                        dangerouslySetInnerHTML={{ __html: msg.content }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="conversation-modal-footer">
          <div className="conversation-info">
            <span className="conversation-thread-id">
              <i className="fas fa-link"></i>
              Thread: {threadId}
            </span>
          </div>
          <button className="btn btn-secondary" onClick={onClose}>
            <i className="fas fa-times"></i>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConversationModal;
